import React, { useState } from 'react';
import {
  HStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { FaPlay, FaPause, FaStepForward, FaStepBackward, FaList } from 'react-icons/fa';
import { useSpotify } from '../context/SpotifyContext';
import { useProgress } from '../context/ProgressContext';
import { spotifyService } from '../services/spotifyService';
import QueueView from './QueueView';

const SpotifyControls: React.FC = () => {
  const { accessToken } = useSpotify();
  const { isPlaying: globalIsPlaying, setIsPlaying, setCurrentTrackId } = useProgress();
  const [localIsPlaying, setLocalIsPlaying] = useState(globalIsPlaying);
  const [showQueue, setShowQueue] = useState(false);

  // Sync local state with global state when it changes
  React.useEffect(() => {
    setLocalIsPlaying(globalIsPlaying);
  }, [globalIsPlaying]);

  const preloadNextTrack = async () => {
    if (!accessToken) return;

    try {
      // Get the queue to see what's coming next
      const queueData = await spotifyService.getQueue(accessToken);
      if (queueData?.queue?.[0]?.id) {
        // Start loading lyrics for the next track
        const nextTrackId = queueData.queue[0].id;
        setCurrentTrackId(nextTrackId);
        console.log('Preloading lyrics for next track:', nextTrackId);
      }
    } catch (error) {
      console.error('Error preloading next track:', error);
    }
  };

  const handlePlaybackAction = async (endpoint: string, method: 'PUT' | 'POST' = 'PUT') => {
    if (!accessToken) return;

    try {
      const response = await fetch(`https://api.spotify.com/v1/me/player/${endpoint}`, {
        method: endpoint === 'next' || endpoint === 'previous' ? 'POST' : method,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Immediately update the local and global state for play/pause actions
      if (endpoint === 'play' || endpoint === 'pause') {
        const newIsPlaying = endpoint === 'play';
        setLocalIsPlaying(newIsPlaying);
        setIsPlaying(newIsPlaying);
      }

      // If it's a skip or previous action, preload the next track and ensure it's playing
      if (endpoint === 'next' || endpoint === 'previous') {
        // Preload the next track
        await preloadNextTrack();
        
        // Ensure the track is playing
        const playResponse = await fetch('https://api.spotify.com/v1/me/player/play', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (playResponse.ok) {
          setLocalIsPlaying(true);
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('Error controlling playback:', error);
    }
  };

  return (
    <>
      <HStack spacing={4} justify="inherit" width="inherit">
        <Tooltip label="Previous track" placement="top">
          <IconButton
            aria-label="Previous track"
            icon={<FaStepBackward />}
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.200' }}
            onClick={() => handlePlaybackAction('previous')}
            size="md"
          />
        </Tooltip>

        <Tooltip label={localIsPlaying ? "Pause" : "Play"} placement="top">
          <IconButton
            aria-label={localIsPlaying ? "Pause" : "Play"}
            icon={localIsPlaying ? <FaPause /> : <FaPlay />}
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.200' }}
            onClick={() => handlePlaybackAction(localIsPlaying ? 'pause' : 'play')}
            size="lg"
            width="60px"
            height="60px"
            fontSize="24px"
          />
        </Tooltip>

        <Tooltip label="Next track" placement="top">
          <IconButton
            aria-label="Next track"
            icon={<FaStepForward />}
            variant="ghost"
            color="white"
            _hover={{ bg: 'whiteAlpha.200' }}
            onClick={() => handlePlaybackAction('next')}
            size="md"
          />
        </Tooltip>
      </HStack>

      {showQueue && (
        <QueueView onClose={() => setShowQueue(false)} />
      )}
    </>
  );
};

export default SpotifyControls; 